// src/components/ProfileCompletionCard.tsx
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "@/redux/hooks/hooks";
import { computeProfileCompletion, pickEmoji } from "@/utils/profileCompletion";

function useAnimatedNumber(target: number, duration = 600) {
  const [value, setValue] = useState(target);

  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const from = value;

    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return value;
}

export default function ProfileCompletionCard({
  currentStep,
}: {
  currentStep: number;
}) {
  const slice = useAppSelector((s) => s.signUpV2); // ✅ store key must be signUpV2
  const { percent } = useMemo(() => computeProfileCompletion(slice), [slice]);

  const animatedPercent = useAnimatedNumber(percent, 650);
  const emoji = useMemo(
    () => pickEmoji(percent, currentStep),
    [percent, currentStep],
  );

  // Preview data (use your real fields)
  const info = slice.SubmitApplicationInfo;
  const v2 = slice.v2Extras;

  const displayName = info.fullName || "";
  const displayAgeGender =
    (info.age ? `${info.age}` : "") + (info.gender ? ` • ${info.gender}` : "");

  const skills = v2.skillsMulti?.length ? v2.skillsMulti : [];
  const socials = v2.profileLinks || [];
  const coreSetup = v2.coreSetup || [];

  const hasAnyData =
    v2.profilePhoto ||
    displayName ||
    displayAgeGender ||
    info.bodyType ||
    skills.length > 0 ||
    v2.portfolioVideos?.length > 0 ||
    info.language ||
    coreSetup.length > 0;

  return (
    <section className="border border-[#D8D8DD] py-[14px] px-4 rounded-2xl bg-white max-w-[265px]">
      {/* top progress */}
      <div className="flex items-center justify-between gap-x-3">
        <div className="min-w-0">
          <p className="text-[#060B13] font-medium text-sm tracking-[-0.7px]">
            Your Profile is{" "}
            <span className="text-[#FF6600]">{animatedPercent}%</span> Complete
          </p>

          <div className="mt-2 h-[7px] w-[171px] rounded-[21px] bg-[#FF66001A] overflow-hidden">
            <div
              className="h-full rounded-[21px] bg-[#FF6600] transition-[width] duration-700 ease-out will-change-[width]"
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>

        <div className="shrink-0 w-10 h-10 rounded-full bg-[#FFF4EC] flex items-center justify-center text-xl">
          {emoji}
        </div>
      </div>

      {/* profile preview */}
      {/* photo */}
      <div
        className={`flex flex-col justify-center items-center gap-[5px] ${
          hasAnyData && "py-5"
        }`}
      >
        {v2.profilePhoto && (
          <div className="size-[84px] rounded-full bg-[#EDEDED] overflow-hidden">
            <img
              src={
                (v2.profilePhoto.live_url ||
                  v2.profilePhoto.local_url) as string
              }
              alt="profile"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {displayName && (
          <p className="text-sm tracking-[-0.7px] font-medium">{displayName}</p>
        )}

        {displayAgeGender && (
          <p className="text-[11px] tracking-[-0.7px] text-[#A6A6A6] font-medium">
            {displayAgeGender}
          </p>
        )}

        {info.bodyType && (
          <p className="text-[#A6A6A6] text-[10px] tracking-[-0.2px]">
            {info.bodyType}
          </p>
        )}

        {/* Skills */}
        {skills.length > 0 && (
          <div className="mt-6 w-full text-center">
            <p className="text-[10px] text-[#A6A6A6] mb-2">Skills</p>
            <div className="flex flex-wrap justify-center gap-2">
              {skills.slice(0, 6).map((sk) => (
                <span
                  key={sk}
                  className="px-2.5 py-1 rounded-full border border-[#EDEDED] text-[10px] text-[#121217]"
                >
                  {sk}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio Videos */}
        {v2.portfolioVideos?.length > 0 && (
          <div className="mt-6 w-full">
            <p className="text-[10px] text-[#A6A6A6] mb-2 text-center">
              Portfolio Videos
            </p>
            <div className="flex justify-center gap-3">
              {v2.portfolioVideos.slice(0, 3).map((video: any, i: number) => (
                <div
                  key={i}
                  className="w-[70px] h-[90px] rounded-[12px] border border-[#EDEDED] bg-[#FAFAFA] overflow-hidden"
                >
                  <video
                    src={video.url}
                    className="w-full h-full object-cover"
                    muted
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Languages */}
        {info.language && (
          <div className="mt-6 w-full text-center">
            <p className="text-[10px] text-[#A6A6A6] mb-2">Languages</p>
            <div className="flex justify-center gap-2 flex-wrap">
              <span className="px-2.5 py-1 rounded-full border border-[#EDEDED] text-[10px]">
                {info.language}
              </span>
            </div>
          </div>
        )}

        {/* Core Setup */}
        {coreSetup.length > 0 && (
          <div className="mt-6 w-full text-center">
            <p className="text-[10px] text-[#A6A6A6] mb-2">Core Setup</p>
            <div className="flex justify-center gap-2 flex-wrap">
              {coreSetup.map((c) => (
                <span
                  key={c.type}
                  className="px-2.5 py-1 rounded-full border border-[#EDEDED] text-[10px] text-[#121217]"
                >
                  {c.type}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
